import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { INbFormGroup } from '../models/form-group';

@Injectable()
export class FormBuilderService {
  constructor(private fb: UntypedFormBuilder) {}

  private getFieldErrors = (formGroup: INbFormGroup) => {
    return (fieldName: string, validatorName?: string) => {
      const control = formGroup.get(fieldName);

      if (!control) {
        return null;
      }

      if (!control.touched || !control.dirty || control.disabled) {
        return null;
      }

      if (validatorName) {
        return control.errors && control.errors[validatorName]
          ? { [validatorName]: control.errors[validatorName] }
          : null;
      }

      return control.errors;
    };
  };

  private isTouched = (formGroup: INbFormGroup) => {
    return (fieldName: string) => {
      const control = formGroup.get(fieldName);

      if (!control) {
        return false;
      }

      return control.touched;
    };
  };

  private isDirty = (formGroup: INbFormGroup) => {
    return (fieldName: string) => {
      if (!fieldName) {
        return !!Object.keys(formGroup.controls).find(field => {
          return formGroup.get(field).dirty;
        });
      }
      const control = formGroup.get(fieldName);

      if (!control) {
        return false;
      }

      return control.dirty;
    };
  };

  private getAllChangedFields = (formGroup: INbFormGroup) => {
    return <T = any>(): T => {
      const obj = {};
      Object.keys(formGroup.controls).forEach(field => {
        if (formGroup.get(field).dirty) {
          obj[field] = formGroup.get(field).value;
        }
      });
      return obj as T;
    };
  };

  private validAllFields = (formGroup: INbFormGroup) => {
    return () => {
      if (formGroup.valid) {
        return true;
      }

      Object.keys(formGroup.controls).forEach(field => {
        const control = formGroup.get(field);
        if (control instanceof UntypedFormControl) {
          control.markAsTouched({ onlySelf: true });
          control.markAsDirty({ onlySelf: true });
        } else if (control instanceof UntypedFormGroup) {
          const fg = control as INbFormGroup;
          if (fg.validAllFields) {
            fg.validAllFields();
          }
        }
      });

      return false;
    };
  };

  group(controlsConfig: { [key: string]: any }, extra?: { [key: string]: any } | null): INbFormGroup {
    const formGroup = this.fb.group(controlsConfig, extra) as INbFormGroup;

    formGroup.getFieldErrors = this.getFieldErrors(formGroup);

    formGroup.isTouched = this.isTouched(formGroup);

    formGroup.isDirty = this.isDirty(formGroup);

    formGroup.getAllChangedFields = this.getAllChangedFields(formGroup);

    formGroup.validAllFields = this.validAllFields(formGroup);

    return formGroup;
  }
}
